import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
// Inter Variable — bundled via npm (@fontsource), no CDN dependency.
import '@fontsource-variable/inter/index.css'
import './index.css'
import App from './App.tsx'

/**
 * Name the component that threw.
 *
 * React's production build replaces every error message with a numeric code
 * ("Minified React error #185"), and the accompanying stack is minified
 * function names. The code identifies the FAILURE CLASS — #185 is "Maximum
 * update depth exceeded", i.e. an infinite render loop — but says nothing
 * about WHERE, and a render loop is usually specific to one route plus one
 * operator's saved filter state, so "reproduce it in dev" is not a reliable
 * way to find it.
 *
 * React 19 hands both root-level error hooks an `errorInfo.componentStack`,
 * which is built from component NAMES, not minified symbols — so it survives
 * minification and points straight at the offending component. Log it next to
 * the error so the browser console carries the answer the first time the bug
 * fires in production, instead of on some later attempt to reproduce it.
 *
 * `onCaughtError` fires when an ErrorBoundary handled it (see
 * components/ErrorBoundary.tsx, which also renders the stack into the
 * fallback); `onUncaughtError` fires when nothing did and the tree unmounts.
 * We want the trail in both cases.
 */
function logComponentStack(kind: string) {
  return (error: unknown, errorInfo: { componentStack?: string | null }) => {
    // Grouped so the (often long) component stack stays readable and can be
    // collapsed, and so it is unambiguously attached to THIS error rather
    // than looking like an unrelated second console line.
    console.error(`React ${kind} error:`, error)
    if (errorInfo?.componentStack) {
      console.error('Component stack:', errorInfo.componentStack)
    }
  }
}

createRoot(document.getElementById('root')!, {
  onCaughtError: logComponentStack('caught'),
  onUncaughtError: logComponentStack('uncaught'),
}).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Re-arm ErrorBoundary's one-shot stale-bundle reload. That guard sets a
// sessionStorage flag so a genuinely broken chunk cannot loop; without clearing
// it, the FIRST stale-bundle reload in a session would also be the LAST, and the
// next deploy would leave this tab showing the error fallback instead of quietly
// reloading. Surviving five seconds means the bundle we are running is intact.
window.setTimeout(() => {
  try { sessionStorage.removeItem('cp:chunk-reload') } catch { /* private mode */ }
}, 5000)
